import * as THREE from 'three'

/**
 * Creates a floating heart element that rises like a balloon
 */
function createFloatingHeart(originX: number, originY: number): void {
  const heart = document.createElement('div')
  heart.className = 'floating-heart'
  
  // Random size
  const size = 16 + Math.random() * 20 // 16-36px
  
  // Random horizontal offset from click point
  const offsetX = (Math.random() - 0.5) * 100 // -50 to +50px
  
  // Random horizontal drift during float
  const driftX = (Math.random() - 0.5) * 150 // -75 to +75px
  
  // Random duration
  const duration = 2 + Math.random() * 1.5 // 2-3.5s
  
  // Random delay for stagger effect
  const delay = Math.random() * 0.2 // 0-0.2s
  
  // Random rotation
  const rotation = (Math.random() - 0.5) * 40 // -20 to +20 degrees
  
  // Set styles
  heart.style.cssText = `
    position: fixed;
    left: ${originX + offsetX}px;
    top: ${originY}px;
    width: ${size}px;
    height: ${size}px;
    pointer-events: none;
    z-index: 1000;
    opacity: 0;
    transform: rotate(${rotation}deg);
    animation: floatUp ${duration}s ease-out ${delay}s forwards;
    --drift-x: ${driftX}px;
  `
  
  // Random pastel color
  const colors = [
    '#D8B4E2', // Light pastel violet
    '#E8B4D8', // Light pastel lavender-pink
    '#FFB6C1', // Light pink
    '#FFC8DD', // Lighter pink
    '#B8E0F6', // Baby blue
    '#A2D2FF', // Soft sky blue
  ]
  const color = colors[Math.floor(Math.random() * colors.length)]
  
  // SVG heart
  heart.innerHTML = `
    <svg viewBox="0 0 100 100" fill="currentColor" style="width: 100%; height: 100%; color: ${color};">
      <path d="M50 88.9C47.8 88.9 45.7 88.1 44.1 86.6C32.9 76.1 22.4 66.3 13.5 55.8C4.1 44.7 0 35.1 0 25.4C0 11.4 11.4 0 25.4 0C34.1 0 42.3 4.1 47.5 10.8L50 14.1L52.5 10.8C57.7 4.1 65.9 0 74.6 0C88.6 0 100 11.4 100 25.4C100 35.1 95.9 44.7 86.5 55.8C77.6 66.3 67.1 76.1 55.9 86.6C54.3 88.1 52.2 88.9 50 88.9Z"/>
    </svg>
  `
  
  document.body.appendChild(heart)
  
  // Remove after animation completes
  setTimeout(() => {
    heart.remove()
  }, (duration + delay) * 1000 + 100)
}

// Heart state for external control
let heartMesh: THREE.Mesh | null = null
let renderer: THREE.WebGLRenderer | null = null
let scene: THREE.Scene | null = null
let camera: THREE.OrthographicCamera | null = null
let animationId: number | null = null

// Rotation state
let rotationY = 0
const baseRotationSpeed = 0.5 // radians per second (default)
let currentRotationSpeed = baseRotationSpeed
const boostAmount = 1.5 // speed added per tap/swipe
const maxSpeed = 12.0 // maximum rotation speed
const decayRate = 0.97 // how fast it returns to normal (per frame, closer to 1 = slower decay)

/**
 * Creates a heart shape path - full, plump cartoon heart
 * Perfectly symmetrical for smooth rotation
 */
function createHeartShape(): THREE.Shape {
  const shape = new THREE.Shape()
  
  // Use parametric approach for perfect symmetry
  // Heart curve based on mathematical heart equation, then smoothed
  const scale = 0.85
  
  // Start at bottom point
  shape.moveTo(0, -0.75 * scale)
  
  // Right side (bottom to top)
  shape.bezierCurveTo(
    0.15 * scale, -0.55 * scale,  // control 1
    0.55 * scale, -0.25 * scale,  // control 2
    0.75 * scale, 0.1 * scale     // end point
  )
  
  // Right lobe
  shape.bezierCurveTo(
    0.95 * scale, 0.4 * scale,    // control 1 - fullness
    0.85 * scale, 0.75 * scale,   // control 2
    0.5 * scale, 0.75 * scale     // top of right lobe
  )
  
  // Right side of center dip
  shape.bezierCurveTo(
    0.25 * scale, 0.75 * scale,   // control 1
    0, 0.6 * scale,               // control 2
    0, 0.5 * scale                // center dip
  )
  
  // Left side of center dip
  shape.bezierCurveTo(
    0, 0.6 * scale,               // control 1
    -0.25 * scale, 0.75 * scale,  // control 2
    -0.5 * scale, 0.75 * scale    // top of left lobe
  )
  
  // Left lobe
  shape.bezierCurveTo(
    -0.85 * scale, 0.75 * scale,  // control 1
    -0.95 * scale, 0.4 * scale,   // control 2 - fullness
    -0.75 * scale, 0.1 * scale    // end point
  )
  
  // Left side (top to bottom)
  shape.bezierCurveTo(
    -0.55 * scale, -0.25 * scale, // control 1
    -0.15 * scale, -0.55 * scale, // control 2
    0, -0.75 * scale              // back to bottom
  )
  
  return shape
}

/**
 * Bakes smooth shading into vertex colors based on face normals
 * Creates soft, rounded look like polished animated hearts
 */
function bakeVertexColors(geometry: THREE.BufferGeometry): void {
  // Compute smooth vertex normals
  geometry.computeVertexNormals()
  
  const normals = geometry.getAttribute('normal')
  const count = normals.count
  
  // Create color attribute
  const colors = new Float32Array(count * 3)
  
  // Light direction (normalized) - simulates light from front-right-top
  const lightDir = new THREE.Vector3(0.4, 0.3, 1).normalize()
  
  // Color range for smooth gradient (dark to bright)
  const darkColor = new THREE.Color(0xC23A5E)   // Shadow color
  const brightColor = new THREE.Color(0xF25672) // Lit color
  
  const normal = new THREE.Vector3()
  const color = new THREE.Color()
  
  for (let i = 0; i < count; i++) {
    // Get vertex normal
    normal.set(
      normals.getX(i),
      normals.getY(i),
      normals.getZ(i)
    )
    
    // Calculate dot product with light direction
    const dot = normal.dot(lightDir)
    
    // Smooth interpolation: map dot product (-1 to 1) to (0 to 1)
    // Add bias towards brighter to keep it looking vibrant
    let t = (dot + 1) * 0.5  // Now 0 to 1
    t = Math.pow(t, 0.7)     // Bias towards brighter
    t = Math.max(0.3, t)     // Don't go too dark
    
    // Smooth lerp between dark and bright
    color.copy(darkColor).lerp(brightColor, t)
    
    colors[i * 3] = color.r
    colors[i * 3 + 1] = color.g
    colors[i * 3 + 2] = color.b
  }
  
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
}

/**
 * Initialize the 3D heart scene
 * Returns a promise that resolves when everything is ready
 */
export function initHeart3D(): Promise<{ start: () => void; stop: () => void }> {
  return new Promise((resolve) => {
    const canvas = document.getElementById('heart-canvas') as HTMLCanvasElement
    if (!canvas) {
      console.warn('Heart canvas not found')
      resolve({ start: () => {}, stop: () => {} })
      return
    }

    const container = canvas.parentElement
    if (!container) {
      resolve({ start: () => {}, stop: () => {} })
      return
    }

    // Get container dimensions
    const rect = container.getBoundingClientRect()
    const width = rect.width || 400
    const height = rect.height || 400

    // Create scene
    scene = new THREE.Scene()

    // Orthographic camera for flat/vector look
    const aspect = width / height
    const frustumSize = 2  // Smaller = heart appears bigger
    camera = new THREE.OrthographicCamera(
      -frustumSize * aspect / 2,
      frustumSize * aspect / 2,
      frustumSize / 2,
      -frustumSize / 2,
      0.1,
      100
    )
    camera.position.z = 5

    // Create renderer with transparency - optimized settings
    renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    })
    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    // Create heart geometry with extrusion for depth
    const heartShape = createHeartShape()
    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: 0.25,
      bevelEnabled: true,
      bevelThickness: 0.12,
      bevelSize: 0.12,
      bevelOffset: 0,
      bevelSegments: 16,  // High segments for very smooth rounded edges
      curveSegments: 64,  // High segments for smooth heart outline
    }
    const geometry = new THREE.ExtrudeGeometry(heartShape, extrudeSettings)
    
    // Center the geometry properly for rotation on ALL axes
    geometry.computeBoundingBox()
    const box = geometry.boundingBox!
    const centerX = (box.max.x + box.min.x) / 2
    const centerY = (box.max.y + box.min.y) / 2
    const centerZ = (box.max.z + box.min.z) / 2
    geometry.translate(-centerX, -centerY, -centerZ)
    
    // Bake smooth shading into vertex colors (no real-time lighting needed)
    bakeVertexColors(geometry)

    // Use MeshBasicMaterial with vertex colors - no lighting calculations
    const material = new THREE.MeshBasicMaterial({
      vertexColors: true,
    })

    // Create heart mesh
    heartMesh = new THREE.Mesh(geometry, material)
    scene.add(heartMesh)

    // No lights needed - shading is baked into vertex colors!

    // Force shader compilation before showing
    renderer.compile(scene, camera)
    
    // Initial render
    renderer.render(scene, camera)

    // Handle resize
    const handleResize = () => {
      if (!container || !camera || !renderer) return
      
      const newRect = container.getBoundingClientRect()
      const newWidth = newRect.width || 400
      const newHeight = newRect.height || 400
      const newAspect = newWidth / newHeight
      
      camera.left = -frustumSize * newAspect / 2
      camera.right = frustumSize * newAspect / 2
      camera.top = frustumSize / 2
      camera.bottom = -frustumSize / 2
      camera.updateProjectionMatrix()
      
      renderer.setSize(newWidth, newHeight)
    }
    
    window.addEventListener('resize', handleResize)

    // Boost function - called on tap/swipe (cumulative)
    const boostRotation = () => {
      currentRotationSpeed = Math.min(currentRotationSpeed + boostAmount, maxSpeed)
    }

    // Spawn floating hearts on interaction
    const spawnFloatingHearts = (originX: number, originY: number) => {
      const heartCount = 3 + Math.floor(Math.random() * 3) // 3-5 hearts
      
      for (let i = 0; i < heartCount; i++) {
        createFloatingHeart(originX, originY)
      }
    }

    // Add touch/click interaction to canvas
    canvas.style.pointerEvents = 'auto'
    canvas.style.cursor = 'pointer'
    
    // Track for swipe detection
    let touchStartX = 0
    let touchStartTime = 0
    
    const handleInteractionStart = (x: number) => {
      touchStartX = x
      touchStartTime = performance.now()
    }
    
    const handleInteractionEnd = (x: number, y: number) => {
      const deltaX = Math.abs(x - touchStartX)
      const deltaTime = performance.now() - touchStartTime
      
      // Detect tap (small movement, quick) or swipe (larger movement)
      if (deltaTime < 300 && deltaX < 20) {
        // Tap
        boostRotation()
        spawnFloatingHearts(x, y)
      } else if (deltaX > 30) {
        // Swipe
        boostRotation()
        spawnFloatingHearts(x, y)
      }
    }
    
    // Mouse events
    canvas.addEventListener('mousedown', (e) => handleInteractionStart(e.clientX))
    canvas.addEventListener('mouseup', (e) => handleInteractionEnd(e.clientX, e.clientY))
    
    // Touch events
    canvas.addEventListener('touchstart', (e) => {
      if (e.touches.length > 0) {
        handleInteractionStart(e.touches[0].clientX)
      }
    }, { passive: true })
    
    canvas.addEventListener('touchend', (e) => {
      if (e.changedTouches.length > 0) {
        handleInteractionEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY)
      }
    }, { passive: true })

    // Animation loop
    let lastTime = performance.now()
    
    const animate = () => {
      animationId = requestAnimationFrame(animate)
      
      const currentTime = performance.now()
      const deltaTime = (currentTime - lastTime) / 1000
      lastTime = currentTime
      
      // Gradually decay speed back to base
      if (currentRotationSpeed > baseRotationSpeed) {
        currentRotationSpeed = baseRotationSpeed + (currentRotationSpeed - baseRotationSpeed) * decayRate
        
        // Snap to base when close enough
        if (currentRotationSpeed - baseRotationSpeed < 0.01) {
          currentRotationSpeed = baseRotationSpeed
        }
      }
      
      if (heartMesh) {
        // Continuous Y-axis rotation (flip effect)
        rotationY += currentRotationSpeed * deltaTime
        heartMesh.rotation.y = rotationY
      }
      
      if (renderer && scene && camera) {
        renderer.render(scene, camera)
      }
    }

    // Control functions
    const start = () => {
      if (animationId === null) {
        lastTime = performance.now()
        animate()
      }
    }

    const stop = () => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId)
        animationId = null
      }
    }

    // Start animation immediately
    start()

    resolve({ start, stop })
  })
}

/**
 * Cleanup function
 */
export function disposeHeart3D(): void {
  if (animationId !== null) {
    cancelAnimationFrame(animationId)
    animationId = null
  }
  
  if (renderer) {
    renderer.dispose()
    renderer = null
  }
  
  if (heartMesh) {
    heartMesh.geometry.dispose()
    if (Array.isArray(heartMesh.material)) {
      heartMesh.material.forEach(m => m.dispose())
    } else {
      heartMesh.material.dispose()
    }
    heartMesh = null
  }
  
  scene = null
  camera = null
}
